import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ClipboardCheck,
  Hammer,
  ListOrdered,
  Pencil,
  Search,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { CatalogItem, ListKind } from "../types/navigator";
import { Badge, statusVariant } from "./Badge";
import { FilterPopover, type FilterOptionGroup } from "./FilterPopover";
import { relativeTime } from "../utils/time";
import { sensitivityInfo } from "../utils/platformSensitivity";

const CHART_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#a855f7",
  "#f59e0b",
  "#ec4899",
  "#14b8a6",
  "#ef4444",
  "#64748b",
  "#0ea5e9",
  "#84cc16",
  "#f97316",
  "#8b5cf6",
];

type ColumnDef = {
  key: string;
  label: string;
  render: (item: CatalogItem) => string;
  className?: string;
  /** When false, the column still shows in the table but is excluded from the
   *  filter popover. Defaults to filterable. */
  filterable?: boolean;
};

const COLUMNS_BY_KIND: Record<ListKind, ColumnDef[]> = {
  initiative: [
    {
      key: "submitter",
      label: "Submitter",
      render: (it) => (it.kind === "initiative" ? it.submittedBy : ""),
    },
    {
      key: "priority",
      label: "Priority",
      render: (it) => (it.kind === "initiative" ? it.priority : ""),
      filterable: false,
    },
    {
      key: "bizScore",
      label: "Business Score",
      render: (it) => (it.kind === "initiative" ? it.businessValueScore : ""),
      filterable: false,
    },
    {
      key: "techScore",
      label: "Technical Score",
      render: (it) =>
        it.kind === "initiative" ? it.technicalFeasibilityScore : "",
      filterable: false,
    },
    {
      key: "avgScore",
      label: "Avg Score",
      render: (it) => {
        if (it.kind !== "initiative") return "";
        if (!it.businessValueScore && !it.technicalFeasibilityScore) return "—";
        const biz = Number(it.businessValueScore) || 0;
        const tech = Number(it.technicalFeasibilityScore) || 0;
        return ((biz + tech) / 2).toFixed(2);
      },
      filterable: false,
    },
  ],
  platform: [
    {
      key: "type",
      label: "Type",
      render: (it) => (it.kind === "platform" ? it.platformType : ""),
    },
    {
      key: "sensitive",
      label: "Data classification",
      render: (it) => (it.kind === "platform" ? sensitivityInfo(it).label : ""),
    },
    {
      key: "license",
      label: "License",
      render: (it) => (it.kind === "platform" ? it.licenseType : ""),
    },
  ],
  library: [
    {
      key: "asset",
      label: "Asset type",
      render: (it) => (it.kind === "library" ? it.assetType : ""),
    },
    {
      key: "platform",
      label: "Platform",
      render: (it) => (it.kind === "library" ? it.aiPlatform : ""),
    },
    {
      key: "dept",
      label: "Department",
      render: (it) => (it.kind === "library" ? it.department : ""),
    },
  ],
};

type StatusFilter = "All" | "Live" | "Other";

function matchesStatusFilter(
  status: string,
  filter: StatusFilter,
  approvedValue: string
): boolean {
  if (filter === "All") return true;
  const s = (status || "").trim().toLowerCase();
  const live = approvedValue.toLowerCase();
  if (filter === "Live") return s === live;
  return s !== live;
}

type Props = {
  kind: ListKind;
  items: CatalogItem[];
  loading: boolean;
  error: string | null;
  approvedValue: string;
  onView: (item: CatalogItem) => void;
  onEdit: (item: CatalogItem) => void;
  onApprove: (item: CatalogItem) => void;
  onDelete: (item: CatalogItem) => void;
  onReview?: (item: CatalogItem) => void;
  /** Only meaningful for initiatives — shows the "Next up" button in the
   *  status chart header when provided. */
  onShowNextUp?: () => void;
  /** Only meaningful for initiatives — shows the "In progress" button in
   *  the status chart header when provided. */
  onShowInProgress?: () => void;
};

export function AdminTable({
  kind,
  items,
  loading,
  error,
  approvedValue,
  onView,
  onEdit,
  onApprove,
  onDelete,
  onReview,
  onShowNextUp,
  onShowInProgress,
}: Props) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const filterTriggerRef = useRef<HTMLButtonElement | null>(null);

  const columns = COLUMNS_BY_KIND[kind];

  // Filter keys are derived from the active kind's columns. When the kind
  // switches, stale keys would silently filter out every row — so clear them.
  useEffect(() => {
    setFilters({});
    setFilterOpen(false);
  }, [kind]);

  function toggleSort(key: string) {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
  }

  const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
    { value: "All", label: "All" },
    { value: "Live", label: approvedValue },
    { value: "Other", label: "Other" },
  ];

  // Build one filter group per data column, tallying how many items carry
  // each distinct value — mirrors the public ListPage filter behaviour.
  const filterGroups: FilterOptionGroup[] = useMemo(() => {
    const colGroups = columns
      .filter((c) => c.filterable !== false)
      .map((c) => {
        const counts = new Map<string, number>();
        for (const it of items) {
          const v = c.render(it);
          if (!v) continue;
          counts.set(v, (counts.get(v) ?? 0) + 1);
        }
        const options = Array.from(counts.entries())
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => a.value.localeCompare(b.value));
        return { key: c.key, label: c.label, options };
      });

    // Status is rendered as its own table column (not in COLUMNS_BY_KIND), so
    // build its filter group from each item's status value.
    const statusCounts = new Map<string, number>();
    for (const it of items) {
      const v = (it.status || "").trim();
      if (!v) continue;
      statusCounts.set(v, (statusCounts.get(v) ?? 0) + 1);
    }
    const statusGroup: FilterOptionGroup = {
      key: "status",
      label: "Status",
      options: Array.from(statusCounts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    };

    return [...colGroups, statusGroup];
  }, [columns, items]);

  const activeFilterCount = useMemo(
    () =>
      Object.values(filters).reduce((sum, arr) => sum + (arr?.length ?? 0), 0),
    [filters]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const activeKeys = Object.keys(filters).filter(
      (k) => (filters[k]?.length ?? 0) > 0
    );
    return items.filter((it) => {
      if (!matchesStatusFilter(it.status, statusFilter, approvedValue))
        return false;
      for (const key of activeKeys) {
        if (key === "status") {
          if (!filters.status.includes((it.status || "").trim())) return false;
          continue;
        }
        const col = columns.find((c) => c.key === key);
        if (!col) continue;
        if (!filters[key].includes(col.render(it))) return false;
      }
      if (!q) return true;
      const hay = `${it.title} ${columns
        .map((c) => c.render(it))
        .join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, statusFilter, columns, approvedValue, filters]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    const valueOf = (it: CatalogItem): string => {
      if (sortKey === "title") return it.title || "";
      if (sortKey === "status") return it.status || "";
      if (sortKey === "updated") return it.modified ?? it.created ?? "";
      return col ? col.render(it) : "";
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      const an = Number(av);
      const bn = Number(bv);
      if (Number.isFinite(an) && Number.isFinite(bn)) {
        return (an - bn) * dir;
      }
      return av.localeCompare(bv, undefined, { sensitivity: "base" }) * dir;
    });
  }, [filtered, sortKey, sortDir, columns]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      All: items.length,
      Live: 0,
      Other: 0,
    };
    const live = approvedValue.toLowerCase();
    for (const it of items) {
      const s = (it.status || "").trim().toLowerCase();
      if (s === live) c.Live += 1;
      else c.Other += 1;
    }
    return c;
  }, [items, approvedValue]);

  // Full per-status breakdown for the chart — unlike the coarse Live/Other
  // toggle above, this is the exact SharePoint Status value per item, only
  // statuses actually present in this tab's data (so a 0-count status never
  // shows an empty slice).
  const statusChartData = useMemo(() => {
    const byStatus = new Map<string, number>();
    for (const it of items) {
      const v = (it.status || "").trim();
      if (!v) continue;
      byStatus.set(v, (byStatus.get(v) ?? 0) + 1);
    }
    return Array.from(byStatus.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [items]);

  // Single-select from the chart, layered onto the same `filters.status`
  // the Filter popover already uses — clicking the active slice again
  // clears it. Picking a status from the chart also resets the coarse
  // Live/Other toggle so the two controls don't fight each other.
  const activeChartStatus =
    filters.status?.length === 1 ? filters.status[0] : undefined;
  const onSelectStatus = (value: string) => {
    setStatusFilter("All");
    setFilters((f) => {
      if (f.status?.length === 1 && f.status[0] === value) {
        const { status: _status, ...rest } = f;
        return rest;
      }
      return { ...f, status: [value] };
    });
  };

  return (
    <div className="admin-table-wrap">
      <AdminStatusDonut
        data={statusChartData}
        activeValue={activeChartStatus}
        onSelect={onSelectStatus}
        onShowNextUp={onShowNextUp}
        onShowInProgress={onShowInProgress}
      />
      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={14} aria-hidden />
          <input
            type="search"
            placeholder={`Search ${items.length} ${kind} items…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search admin list"
          />
        </div>
        <div className="admin-status-tabs" role="tablist">
          {STATUS_FILTERS.map((s) => {
            const active = s.value === statusFilter;
            return (
              <button
                key={s.value}
                type="button"
                role="tab"
                aria-selected={active}
                className={`admin-status-tab${active ? " is-active" : ""}`}
                onClick={() => setStatusFilter(s.value)}
              >
                <span>{s.label}</span>
                {s.value === "All" ? null : (
                  <span className="admin-status-count">{counts[s.value]}</span>
                )}
              </button>
            );
          })}
        </div>
        <button
          ref={filterTriggerRef}
          type="button"
          className={`filter-trigger${
            activeFilterCount > 0 ? " has-filters" : ""
          }`}
          onClick={() => setFilterOpen((o) => !o)}
          aria-expanded={filterOpen}
          aria-haspopup="dialog"
        >
          <SlidersHorizontal size={16} />
          <span>Filter</span>
          {activeFilterCount > 0 ? (
            <span className="filter-trigger-count">{activeFilterCount}</span>
          ) : null}
        </button>
      </div>

      <FilterPopover
        open={filterOpen}
        anchor={filterTriggerRef.current}
        groups={filterGroups}
        initialValues={filters}
        onApply={setFilters}
        onClose={() => setFilterOpen(false)}
      />

      {error ? (
        <div className="error-banner" role="alert">
          <strong>Couldn't load.</strong> {error}
        </div>
      ) : null}

      <div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh
                label="Title"
                sortKey="title"
                activeKey={sortKey}
                dir={sortDir}
                onSort={toggleSort}
                className="admin-th-title"
              />
              {columns.map((c) => (
                <SortableTh
                  key={c.key}
                  label={c.label}
                  sortKey={c.key}
                  activeKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                  className={c.className}
                />
              ))}
              <SortableTh
                label="Status"
                sortKey="status"
                activeKey={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              />
              <SortableTh
                label="Updated"
                sortKey="updated"
                activeKey={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              />
              <th scope="col" className="admin-th-actions">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows colSpan={columns.length + 4} />
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 4} className="admin-empty">
                  {query ? "No matches" : `No items`}
                </td>
              </tr>
            ) : (
              sorted.map((item) => {
                const status = item.status || "Pending";
                const isApproved =
                  status.trim().toLowerCase() === approvedValue.toLowerCase();
                return (
                  <tr key={`${item.kind}-${item.id}`}>
                    <td className="admin-td-title">
                      <button
                        type="button"
                        className="admin-title-btn"
                        onClick={() => onView(item)}
                        title="Open details"
                      >
                        {item.title || "Untitled"}
                      </button>
                    </td>
                    {columns.map((c) => (
                      <td key={c.key} className={c.className}>
                        <span className="admin-td-text">
                          {c.render(item) || "—"}
                        </span>
                      </td>
                    ))}
                    <td>
                      <Badge variant={statusVariant(status)}>{status}</Badge>
                    </td>
                    <td className="admin-td-time">
                      {relativeTime(item.modified ?? item.created) || "—"}
                    </td>
                    <td className="admin-td-actions">
                      <button
                        type="button"
                        className="admin-action-btn"
                        onClick={() => onEdit(item)}
                        title="Edit"
                        aria-label="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      {onReview && item.kind === "initiative" ? (
                        <button
                          type="button"
                          className="admin-action-btn admin-action-review"
                          onClick={() => onReview(item)}
                          title="Business & Technical review"
                          aria-label="Review"
                        >
                          <ClipboardCheck size={15} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="admin-action-btn admin-action-approve"
                        onClick={() => onApprove(item)}
                        disabled={isApproved}
                        title={
                          isApproved
                            ? `Already ${approvedValue.toLowerCase()}`
                            : `Mark ${approvedValue}`
                        }
                        aria-label={`Mark ${approvedValue}`}
                      >
                        <CheckCircle2 size={15} />
                      </button>
                      <button
                        type="button"
                        className="admin-action-btn admin-action-delete"
                        onClick={() => onDelete(item)}
                        title="Delete"
                        aria-label="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminStatusDonut({
  data,
  activeValue,
  onSelect,
  onShowNextUp,
  onShowInProgress,
}: {
  data: { name: string; value: number }[];
  activeValue?: string;
  onSelect: (value: string) => void;
  onShowNextUp?: () => void;
  onShowInProgress?: () => void;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;
  return (
    <div className="admin-status-chart">
      <div className="admin-status-chart-head">
        <div className="admin-status-chart-title">Status overview</div>
        <div className="admin-status-chart-head-actions">
          {onShowInProgress ? (
            <button
              type="button"
              className="btn btn-ghost admin-backlog-btn"
              onClick={onShowInProgress}
            >
              <Hammer size={14} />
              In progress
            </button>
          ) : null}
          {onShowNextUp ? (
            <button
              type="button"
              className="btn btn-ghost admin-backlog-btn"
              onClick={onShowNextUp}
            >
              <ListOrdered size={14} />
              Next up
            </button>
          ) : null}
        </div>
      </div>
      <div className="admin-status-chart-body">
        <div className="admin-status-chart-donut-wrap">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={64}
                outerRadius={94}
                paddingAngle={3}
                cornerRadius={6}
                stroke="none"
                onClick={(p: { name?: string }) => p?.name && onSelect(p.name)}
                style={{ cursor: "pointer" }}
              >
                {data.map((d, i) => {
                  const isActive = activeValue === d.name;
                  const isDimmed = !!activeValue && !isActive;
                  return (
                    <Cell
                      key={d.name}
                      fill={CHART_COLORS[i % CHART_COLORS.length]}
                      fillOpacity={isDimmed ? 0.3 : 1}
                      stroke={isActive ? "var(--text, #111)" : "none"}
                      strokeWidth={isActive ? 2 : 0}
                    />
                  );
                })}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="admin-status-chart-center" aria-hidden>
            <span className="admin-status-chart-center-value">{total}</span>
            <span className="admin-status-chart-center-label">Total</span>
          </div>
        </div>
        <ul className="admin-status-chart-legend-grid">
          {data.map((d, i) => {
            const isActive = activeValue === d.name;
            const isDimmed = !!activeValue && !isActive;
            const pct = Math.round((d.value / total) * 100);
            const color = CHART_COLORS[i % CHART_COLORS.length];
            return (
              <li
                key={d.name}
                className={`admin-status-chart-legend-item${isActive ? " is-active" : ""}`}
                onClick={() => onSelect(d.name)}
                style={{ opacity: isDimmed ? 0.5 : 1 }}
              >
                <span className="admin-status-chart-legend-row">
                  <span className="insight-legend-swatch" style={{ background: color }} />
                  <span className="admin-status-chart-legend-name">{d.name}</span>
                  <span className="admin-status-chart-legend-pct">{pct}%</span>
                  <span className="admin-status-chart-legend-count" style={{ background: color }}>
                    {d.value}
                  </span>
                </span>
                <span className="admin-status-chart-legend-bar">
                  <span
                    className="admin-status-chart-legend-bar-fill"
                    style={{ width: `${pct}%`, background: color }}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function SortableTh({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  className,
}: {
  label: string;
  sortKey: string;
  activeKey: string | null;
  dir: "asc" | "desc";
  onSort: (key: string) => void;
  className?: string;
}) {
  const active = activeKey === sortKey;
  return (
    <th scope="col" className={className} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className={`admin-sort-btn${active ? " is-active" : ""}`}
        onClick={() => onSort(sortKey)}
        title={`Sort by ${label}`}
      >
        <span>{label}</span>
        {active ? (
          dir === "asc" ? (
            <ArrowUp size={13} aria-hidden />
          ) : (
            <ArrowDown size={13} aria-hidden />
          )
        ) : (
          <ArrowUpDown size={13} className="admin-sort-idle" aria-hidden />
        )}
      </button>
    </th>
  );
}

function SkeletonRows({ colSpan }: { colSpan: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="admin-skeleton-row">
          <td colSpan={colSpan}>
            <span className="admin-skeleton-bar" />
          </td>
        </tr>
      ))}
    </>
  );
}